import React, { useState, useRef, useEffect } from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';
import CustomTooltip from '../../../components/tooltip/CustomTooltip';
import { useNavigate } from 'react-router-dom';

export default function CombinedStatCard({ cardMetaData }) {
  const navigate = useNavigate();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [clickedOpen, setClickedOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!clickedOpen) return;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setClickedOpen(false);
        setIsTooltipOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clickedOpen]);

  const renderIcon = () => {
    if (cardMetaData.icon && PhosphorIcons[cardMetaData.icon]) {
      return React.createElement(PhosphorIcons[cardMetaData.icon], {
        size: 16,
        color: cardMetaData.color,
      });
    }
    return null;
  };

  return (
    <CustomTooltip
      text={cardMetaData.tooltipText}
      open={isTooltipOpen || clickedOpen}
      onOpenChange={(val) => {
        if (!clickedOpen) {
          setIsTooltipOpen(val);
        }
      }}
    >
      <div
        ref={wrapperRef}
        className={`border border-[var(--q3-stroke-light)] rounded-xl px-3 py-3 flex flex-row lg:flex-col gap-4 justify-between ${
          cardMetaData.viewButton && cardMetaData.buttonLink ? 'cursor-pointer' : ''
        }`}
        onClick={() => {
          if (cardMetaData.viewButton && cardMetaData.buttonLink) {
            navigate(cardMetaData.buttonLink);
          }
        }}
      >
        <div className="hidden lg:flex flex-col items-start gap-2">
          <div className="flex flex-row items-center justify-between w-full">
            {renderIcon()}
            <div className="flex items-center shrink-0 ml-1">
              {cardMetaData.viewButton ? (
                <PhosphorIcons.CaretRightIcon size={16} color="var(--q3-neutral-light)" className="cursor-pointer" />
              ) : (
                <PhosphorIcons.InfoIcon
                  size={16}
                  color="var(--q3-neutral-light)"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = !clickedOpen;
                    setClickedOpen(next);
                    setIsTooltipOpen(next);
                  }}
                />
              )}
            </div>
          </div>

          <span className="text-[var(--q3-neutral-light)] text-[10px] lg:text-xs font-semibold leading-tight line-clamp-2 ">
            {cardMetaData.title}
          </span>
        </div>

        <div className="flex lg:hidden flex-row items-center gap-2">
          <div className="flex flex-row items-center gap-2 ">
            {renderIcon()}
            <span className="text-[var(--q3-neutral-light)] text-xs font-semibold leading-tight line-clamp-2 max-w-[150px]">
              {cardMetaData.title}
            </span>
          </div>
          <div className="flex items-center shrink-0 ml-1">
            {cardMetaData.viewButton ? (
              <PhosphorIcons.CaretRightIcon size={16} color="var(--q3-neutral-light)" className="cursor-pointer" />
            ) : (
              <PhosphorIcons.InfoIcon
                size={16}
                color="var(--q3-neutral-light)"
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  const next = !clickedOpen;
                  setClickedOpen(next);
                  setIsTooltipOpen(next);
                }}
              />
            )}
          </div>
        </div>

        <div className="flex flex-row items-end justify-between mt-1">
          <p className="text-[var(--q3-neutral-default)] font-bold text-lg lg:text-2xl leading-none">
            {cardMetaData.numerator}
            {cardMetaData.isPercentage && '%'}
            {cardMetaData.denominator && (
              <span className="text-[var(--q3-neutral-light)] text-xs lg:text-sm font-normal">
                {!cardMetaData.isTime && '/'}
                {cardMetaData.denominator}
              </span>
            )}
          </p>
        </div>
      </div>
    </CustomTooltip>
  );
}
